import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';

import auth from '../../../../core/authorization';

import { useSnackbar } from 'notistack';
import { IAnnouncementImport, IUpdateAnnouncementResult } from '../../../../interfaces/IAnnouncement';
import { AnnouncementTypeEnum, DialogModeEnum } from '../../../../interfaces/enums';
import {
    IAnimalSummary,
    IAnimalSummaryExtraInfo,
    IFarmAnimal,
} from '../../../../interfaces/IAnimal';
import { IFarmDetails } from '../../../../interfaces/IBusinessEntities';
import { ITiltuApiResult } from '../../../../interfaces/IGeneral';
import { ResourceTextApplication } from '../../../../core/resources';

import BovineSlaughterAnnouncement from '../bovine-slaughter-announcement/BovineSlaughterAnnouncement';
import BovineIntermediationAnnouncement from '../bovine-intermediation-announcement/BovineIntermediationAnnouncement';
import { fetchCanCreateAnimalAnnouncements } from '../../../shared/validators/canCreateAnnouncementValidator';
import utils from '../../../../core/utils';
import moment from 'moment';
import { IMainGroup } from '../../../../interfaces/IGroup';
import groupsApi from '../../../../api/groupsApi';
import log from '../../../../core/log';
import { getAnimalGroup } from '../../helpers/data';

export interface IAnnouncementImportProps {
    data: IAnnouncementImport;
    farmData: IFarmDetails | null;
    animalData: IAnimalSummaryExtraInfo[];

    onClose: () => void;
    processAnnouncementSave: (
        data: IUpdateAnnouncementResult,
        type: AnnouncementTypeEnum,
        holdingSiteId: string
    ) => void;
    mapUpdatedSalesTypeToAllAnimalsData: (data: IFarmAnimal[]) => void;
}

export default function AnnouncementImport(props: IAnnouncementImportProps) {
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation<ResourceTextApplication[]>(['AnelmaGeneral', 'AnelmaBovine', 'AnelmaSolmuErrors']);

    const [selectedAnimals, setSelectedAnimals] = useState<IAnimalSummaryExtraInfo[]>([]);
    const [animalGroups, setAnimalGroups] = useState<IMainGroup[] | null>(null);
    const [canCreateAnimalAnnouncement, setCanCreateAnimalAnnouncement] = useState<boolean | null>(
        null
    );

    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        if (!props.data) return;
        if (!props.farmData || !props.farmData.Id) return;
        if (!props.animalData) return;
        if (props.farmData.Aliases.find((x) => x.Type === 3)?.Value !== props.data.FarmId) return;

        fetchGroupsData();
    }, [props.data, props.farmData, props.animalData]);

    useEffect(() => {
        const animals: IAnimalSummaryExtraInfo[] = [];
        let animalIsMissing = false;

        for (let euIdentifier of props.data.EUIdentifiers) {
            const animal = props.animalData.find((x) => x.EuIdentifier == euIdentifier);

            if (animal) {
                animals.push(animal as IAnimalSummaryExtraInfo);
            } else {
                animalIsMissing = true;
            }
        }

        if (animalIsMissing)
            enqueueSnackbar(t('AnelmaBovine:1161'), {
                variant: 'error',
            });

        if (animals.length > 0) {
            setSelectedAnimals(countExternalFields(animals));
        }

        if (props.farmData && props.farmData.Id)
            fetchCanCreateAnimalAnnouncements(props.farmData.Id, setCanCreateAnimalAnnouncement, enqueueSnackbar);
    }, [animalGroups]);

    const fetchGroupsData = () => {
        if (props.farmData?.Id) {
            groupsApi
                .getGroups(String(props.farmData.Id))
                .then((response) => {
                    if (response?.Items) {
                        setAnimalGroups(response.Items);
                    }
                })
                .catch((err) => {
                    enqueueSnackbar(t('AnelmaGeneral:1020'), {
                        variant: 'error',
                    });
                    return;
                })
                .finally(() => setLoading(false));
        }
    };

    const countExternalFields = (animals: IAnimalSummaryExtraInfo[]) => {
        if (animals !== null) {
            let res = [...animals];

            res.forEach((a, i) => {
                a.Eu4 =
                    a.EuIdentifier?.length > 0
                        ? a.EuIdentifier.substring(a.EuIdentifier.length - 6, a.EuIdentifier.length - 2)
                        : null;

                a.AgeMonths = utils.calculateAgeInMonths(moment(), moment(a.BirthDate), 30.33);
                a.Group = getAnimalGroup(a.Id, animalGroups);
            });

            return res;
        }

        return [];
    }

    const onClose = () => {
        window.location.href = '/animals/list';
        props.onClose();
    };

    const processAnnouncementSave = (
        data: IUpdateAnnouncementResult,
        type: AnnouncementTypeEnum,
        holdingSiteId: string
    ) => {
        props.processAnnouncementSave(data, type, holdingSiteId);
        if (type !== AnnouncementTypeEnum.SlaughterWithChainInformation) onClose();
    };

    switch (props.data?.AnimalAnnouncementType) {
        default:
            return <></>;
        case AnnouncementTypeEnum.Slaughter:
            return (
                <BovineSlaughterAnnouncement
                    data={null}
                    onClose={onClose}
                    mode={DialogModeEnum.Create}
                    selectedAnimals={selectedAnimals.length == 0 ? null : selectedAnimals}
                    selectedHoldingSites={props.data.HoldingPlaces}
                    animals={props.animalData.length == 0 ? null : props.animalData}
                    processAnnouncementSave={processAnnouncementSave}
                    processAnnouncementDelete={() => false}
                    mapUpdatedSalesTypeToAllAnimalsData={props.mapUpdatedSalesTypeToAllAnimalsData}
                    loading={loading}
                    canCreateAnimalAnnouncement={canCreateAnimalAnnouncement}
                    dataImportedViaMmfi={true}
                    readOnly={false}
                />
            );
        case AnnouncementTypeEnum.Intermediation:
            return (
                <BovineIntermediationAnnouncement
                    data={null}
                    onClose={onClose}
                    mode={DialogModeEnum.Create}
                    selectedAnimals={selectedAnimals.length == 0 ? null : selectedAnimals}
                    selectedHoldingSites={props.data.HoldingPlaces}
                    animals={props.animalData.length == 0 ? null : props.animalData}
                    processAnnouncementSave={processAnnouncementSave}
                    processAnnouncementDelete={() => false}
                    mapUpdatedSalesTypeToAllAnimalsData={props.mapUpdatedSalesTypeToAllAnimalsData}
                    loading={loading}
                    canCreateAnimalAnnouncement={canCreateAnimalAnnouncement}
                    dataImportedViaMmfi={true}
                    readOnly={false}
                />
            );
    }
}
